import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { CheckCheck, Keyboard, Loader2, ScanLine } from 'lucide-react'
import { cn } from '@/lib/utils'
import { explain } from '@/shared/api/problem'
import { ErpTable, MetricCard, Td, Th } from '@/components/erp/primitives'
import { EmptyState, ErrorStrip, LoadingRows, Note } from '@/shared/ui/states'
import { Modal } from '@/shared/ui/Modal'
import { fmtDateTime, fmtNumber } from '@/shared/lib/format'
import { useElectionContext } from '@/shared/election/context'
import { NoElectionSelected } from '@/shared/election/ElectionSelector'
import { useCan } from '@/shared/auth/session'
import { getReturn, listReturns, verifyReturn, type TallyReturn } from './api'
import { ReturnStatusChip, formLabel, seatLabel } from './vocab'

/**
 * The register of claims: every return filed, however it got here.
 *
 * ## Why this page has to exist
 *
 * A return is filed at `SUBMITTED`, and boards count `VERIFIED` and `CERTIFIED` only — so a
 * freshly entered return is deliberately invisible on every board until a second person has
 * checked it. Without a page that lists `SUBMITTED`, that correct behaviour is
 * indistinguishable from the figures having been lost: the Live board says "no candidate
 * figures counted yet", which is true and useless, and there is no screen anywhere that says
 * "they are here, waiting for you".
 *
 * This is that screen, and it is also the only place verification can be started from. The
 * Certificates desk lists `VERIFIED` and `CERTIFIED`, so the queue of things NEEDING
 * verification was never fetched by anything.
 *
 * ## Keyed and scanned returns are the same kind of thing
 *
 * `sourceCode` is shown on every row because it is the first question asked when a figure
 * turns out to be wrong — a `FORM_OCR` return is checked against the image, an `AGENT_ENTRY`
 * one with the person who keyed it. It is a provenance column, not a quality ranking.
 */
export function ReturnsPage() {
  const { electionId } = useElectionContext()
  // The waiver is a permission, so the note below states what THIS account can actually do
  // rather than a general rule that may not apply to the reader. A super-user holds it via
  // the wildcard, which the server expands to concrete authorities before we see them.
  const mayVerifyOwn = useCan('tally:verify-own')
  const [params, setParams] = useSearchParams()
  const status = params.get('status') ?? 'SUBMITTED'
  const openId = params.get('return')

  const [rows, setRows] = useState<TallyReturn[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [detail, setDetail] = useState<TallyReturn | null>(null)

  const load = useCallback(async () => {
    if (!electionId) { setRows([]); return }
    try {
      setError(null)
      setRows(await listReturns({
        electionId,
        status: status === 'ALL' ? null : status,
        size: 300,
      }))
    } catch (err) {
      setError(explain(err))
      setRows([])
    }
  }, [electionId, status])

  useEffect(() => { void load() }, [load])

  useEffect(() => {
    if (!openId) { setDetail(null); return }
    let live = true
    void (async () => {
      try {
        const r = await getReturn(openId)
        if (live) setDetail(r)
      } catch (err) {
        if (live) setError(explain(err))
      }
    })()
    return () => { live = false }
  }, [openId])

  if (!electionId) return <NoElectionSelected what="A returns register" />

  const verify = async (id: string) => {
    setBusyId(id)
    setError(null)
    try {
      await verifyReturn(id)
      await load()
      if (openId === id) setDetail(await getReturn(id))
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusyId(null)
    }
  }

  const setStatus = (s: string) => setParams((p) => {
    p.set('status', s); p.delete('return'); return p
  }, { replace: true })

  const open = (id: string | null) => setParams((p) => {
    if (id) p.set('return', id); else p.delete('return')
    return p
  }, { replace: true })

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-erp-base font-semibold">Returns</h2>
          <p className="text-erp-xs text-muted-foreground mt-0.5">
            Every set of figures filed for this election, keyed or scanned, and where each one
            has reached on the way to being declared.
          </p>
        </div>
        <div className="axis-table-scroll flex items-center gap-1.5">
          {FILTERS.map((f) => (
            <button key={f.code} type="button" onClick={() => setStatus(f.code)}
              className={cn('h-8 px-2.5 rounded-md border text-erp-xs whitespace-nowrap',
                status === f.code
                  ? 'border-chocolate bg-chocolate-soft/40 text-chocolate font-medium'
                  : 'border-input text-muted-foreground hover:text-foreground')}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-2"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
        <MetricCard label={FILTERS.find((f) => f.code === status)?.label ?? 'Returns'}
          value={fmtNumber(rows?.length ?? 0)} />
      </div>

      <ErrorStrip message={error} className="rounded-md border" />

      {status === 'SUBMITTED' && rows !== null && rows.length > 0 && (
        <Note tone="info">
          These are filed but <strong>not counted anywhere yet</strong>. Boards count verified
          and certified returns only, so each of these has to be checked against the paper
          {mayVerifyOwn ? ' ' : ' by somebody other than whoever entered it '}
          before it reaches the Live board.
        </Note>
      )}

      {rows !== null && rows.length === 0 && !error && (
        <EmptyState
          title="Nothing here"
          hint={status === 'SUBMITTED'
            ? 'No return is waiting for verification. Key a form in or commit a scan and it '
              + 'will appear here first.'
            : 'No return has reached this status yet.'} />
      )}

      {(rows === null || rows.length > 0) && (
        <ErpTable head={(
          <>
            <Th>Place</Th>
            <Th>Race</Th>
            <Th>Entered</Th>
            <Th>Status</Th>
            <Th className="text-right">Valid</Th>
            <Th className="text-right">Cast</Th>
            <Th className="text-right">Turnout</Th>
            <Th>Filed</Th>
            <Th />
          </>
        )}>
          {rows === null ? <LoadingRows colSpan={9} />
            : rows.map((r) => (
              <tr key={r.id}>
                <Td>
                  {r.unitName ?? '—'}
                  {r.presidingOfficerName && (
                    <span className="block text-erp-xs text-muted-foreground truncate">
                      {r.presidingOfficerName}
                    </span>
                  )}
                </Td>
                <Td>
                  {seatLabel(r.seatTypeCode)}
                  <span className="block text-erp-xs text-muted-foreground">
                    {formLabel(r.formTypeCode)}
                    {r.versionNo > 1 && ` · version ${r.versionNo}`}
                  </span>
                </Td>
                <Td><SourceChip code={r.sourceCode} /></Td>
                <Td>
                  <ReturnStatusChip code={r.statusCode} />
                  {r.pendingVariances > 0 && (
                    <span className="block mt-0.5 text-erp-xs text-amber-700 dark:text-amber-400">
                      {r.pendingVariances} unsettled
                    </span>
                  )}
                </Td>
                <Td className="text-right tabular-nums font-medium">{fmtNumber(r.validVotes)}</Td>
                <Td className="text-right tabular-nums">{fmtNumber(r.totalCast)}</Td>
                <Td className={cn('text-right tabular-nums',
                  r.overSubscribed && 'text-red-700 dark:text-red-400 font-medium')}>
                  {r.turnoutPct == null ? '—' : `${r.turnoutPct}%`}
                </Td>
                <Td className="text-erp-xs text-muted-foreground">
                  {r.submittedAt ? fmtDateTime(r.submittedAt) : '—'}
                </Td>
                <Td>
                  <div className="flex items-center gap-2 justify-end">
                    {busyId === r.id && <Loader2 className="h-3 w-3 animate-spin" />}
                    {r.statusCode === 'SUBMITTED' && (
                      <button type="button" disabled={busyId !== null}
                        onClick={() => void verify(r.id)}
                        className="inline-flex items-center gap-1 text-erp-xs text-chocolate
                                   hover:underline">
                        <CheckCheck className="h-3 w-3" /> Verify
                      </button>
                    )}
                    <button type="button" onClick={() => open(r.id)}
                      className="text-erp-xs text-chocolate hover:underline">
                      Open
                    </button>
                  </div>
                </Td>
              </tr>
            ))}
        </ErpTable>
      )}

      <Note tone="info">
        {mayVerifyOwn ? (
          <>
            Verification normally refuses the person who filed the return — a check you perform
            on your own work is the same reading twice. <strong>Your account holds
            {' '}<code>tally:verify-own</code></strong>, so you can verify your own entries and
            run the whole ladder alone. Each time you do, the audit trail marks that return as
            self-verified, so it stays distinguishable from one two people checked.
          </>
        ) : (
          <>
            Verification refuses the person who filed the return — a check you perform on your
            own work is the same reading twice. If the Verify button returns an error saying so,
            it is working: somebody else has to do it, or the account needs
            {' '}<code>tally:verify-own</code>.
          </>
        )}
      </Note>

      <ReturnDetail row={detail} loading={openId !== null && detail === null}
        onClose={() => open(null)} />
    </div>
  )
}

// ── detail ───────────────────────────────────────────────────────────────────────────────

/** Everything the paper said, as this system recorded it. */
function ReturnDetail({ row, loading, onClose }: {
  row: TallyReturn | null
  loading: boolean
  onClose: () => void
}) {
  return (
    <Modal open={loading || row !== null} onClose={onClose} width="lg"
      title={row ? `${formLabel(row.formTypeCode)} · ${row.unitName ?? ''}` : 'Return'}>
      {row === null ? (
        <div className="py-8 text-center text-erp-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin mx-auto mb-2" /> Loading
        </div>
      ) : (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            <ReturnStatusChip code={row.statusCode} />
            <SourceChip code={row.sourceCode} />
            {row.versionNo > 1 && (
              <span className="text-erp-xs text-muted-foreground">
                version {row.versionNo}
              </span>
            )}
          </div>

          <section>
            <h3 className="text-erp-sm font-semibold mb-2">Votes for each candidate</h3>
            {row.candidates.length === 0 ? (
              <p className="text-erp-xs text-muted-foreground">
                No candidate figures on this return.
              </p>
            ) : (
              <div className="rounded-md border border-border divide-y divide-border">
                {row.candidates.map((c) => (
                  <div key={c.candidacyId}
                    className="flex items-center gap-3 px-3 py-1.5 text-erp-sm">
                    <span className="flex-1 min-w-0 truncate">
                      {c.ballotName ?? '—'}
                      <span className="ml-2 text-erp-xs text-muted-foreground">
                        {c.independent ? 'Independent' : (c.partyShortName ?? '')}
                      </span>
                    </span>
                    <span className="tabular-nums font-medium">{fmtNumber(c.votes)}</span>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section>
            <h3 className="text-erp-sm font-semibold mb-2">Statistics</h3>
            <div className="grid gap-2"
              style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))' }}>
              <Stat label="Registered" value={row.registeredVoters} />
              <Stat label="Valid" value={row.validVotes} />
              <Stat label="Rejected" value={row.rejectedVotes} />
              <Stat label="Spoilt" value={row.spoiltVotes} />
              <Stat label="Disputed" value={row.disputedVotes} />
              <Stat label="Total cast" value={row.totalCast} />
            </div>
            {row.overSubscribed && (
              <Note tone="bad">
                More ballots cast than registered voters. Recorded, not refused — it is waiting
                at the reconciliation desk.
              </Note>
            )}
          </section>

          {(row.presidingOfficerName || row.deputyOfficerName || row.declaredAt
            || row.agents.length > 0) && (
            <section>
              <h3 className="text-erp-sm font-semibold mb-2">Declaration</h3>
              <div className="grid gap-2 mb-2"
                style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
                <Field label="Presiding officer" value={row.presidingOfficerName} />
                <Field label="Deputy" value={row.deputyOfficerName} />
                <Field label="Declared at"
                  value={row.declaredAt ? fmtDateTime(row.declaredAt) : null} />
              </div>

              {row.agents.length > 0 && (
                <div className="rounded-md border border-border divide-y divide-border">
                  {row.agents.map((a) => (
                    <div key={a.id} className="px-3 py-1.5 text-erp-sm">
                      <div className="flex items-center gap-2">
                        <span className="flex-1 min-w-0 truncate">{a.name}</span>
                        <span className="text-erp-xs text-muted-foreground truncate">
                          {a.represents ?? a.ballotName ?? '—'}
                        </span>
                        <span className={cn('text-erp-xs',
                          a.signed ? 'text-emerald-700 dark:text-emerald-400'
                            : 'text-amber-700 dark:text-amber-400')}>
                          {a.signed ? 'signed' : 'refused'}
                        </span>
                      </div>
                      {!a.signed && a.refusalReason && (
                        <p className="text-erp-xs text-muted-foreground mt-0.5">
                          {a.refusalReason}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {row.correctionReason && (
            <Note tone="info">
              Filed as a correction: {row.correctionReason}
            </Note>
          )}
        </div>
      )}
    </Modal>
  )
}

// ── bits ─────────────────────────────────────────────────────────────────────────────────

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <span className="block text-erp-xs text-muted-foreground">{label}</span>
      <span className="block text-erp-sm font-semibold tabular-nums">{fmtNumber(value)}</span>
    </div>
  )
}

function Field({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <span className="block text-erp-xs text-muted-foreground">{label}</span>
      <span className="block text-erp-sm">{value ?? '—'}</span>
    </div>
  )
}

/** Provenance, not a quality ranking — see the page docstring. */
function SourceChip({ code }: { code: string }) {
  const keyed = code === 'AGENT_ENTRY'
  return (
    <span className="inline-flex items-center gap-1 text-erp-xs text-muted-foreground">
      {keyed ? <Keyboard className="h-3 w-3" /> : <ScanLine className="h-3 w-3" />}
      {keyed ? 'Keyed in' : code === 'FORM_OCR' ? 'From a scan' : code}
    </span>
  )
}

/*
   SUBMITTED first, and it is the default view.

   It is the only status with work attached to it — everything else on this page is a record
   of work already done. A register that opens on "all" buries the queue in the archive.
*/
const FILTERS = [
  { code: 'SUBMITTED', label: 'Awaiting verification' },
  { code: 'VERIFIED', label: 'Verified' },
  { code: 'CERTIFIED', label: 'Declared' },
  { code: 'DISPUTED', label: 'Disputed' },
  { code: 'REJECTED', label: 'Rejected' },
  { code: 'ALL', label: 'All' },
]
